import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Clock } from "lucide-react";
import { fmtIDR, imgSrc } from "../lib/api";
import { fmtUSD, fmtUSDT } from "../lib/currency";
import { useI18n } from "../lib/i18n";

function CopyRow({ label, value, display, testId }) {
  const copy = () => navigator.clipboard.writeText(String(value)).then(() => toast.success("Disalin"));
  return (
    <div className="flex items-center justify-between gap-2 bg-[#191A1F] border border-[#35353B] rounded-sm px-3 py-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#A8A8AD]">{label}</div>
        <div data-testid={testId} className="font-mono text-sm text-[#D4AF37] break-all">{display ?? value}</div>
      </div>
      <button data-testid={`${testId}-copy`} onClick={copy} className="text-[#A8A8AD] hover:text-[#D4AF37] transition-colors shrink-0"><Copy size={15} /></button>
    </div>
  );
}

function PayCountdown({ deadline, onExpire }) {
  const { t } = useI18n();
  const [left, setLeft] = useState(() => new Date(deadline) - Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      const ms = new Date(deadline) - Date.now();
      setLeft(ms);
      if (ms <= -35000) { clearInterval(id); onExpire && onExpire(); }
    }, 1000);
    return () => clearInterval(id);
  }, [deadline, onExpire]);
  const s = Math.max(0, Math.floor(left / 1000));
  const hh = Math.floor(s / 3600), mm = Math.floor((s % 3600) / 60), ss = s % 60;
  const txt = `${hh > 0 ? `${hh}:` : ""}${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return (
    <div data-testid="pay-countdown" className={`flex items-center gap-2 rounded-sm px-3 py-2 border ${s < 300 ? "border-[#F06464] text-[#F06464]" : "border-[#D4AF37] text-[#F2D98D]"}`}>
      <Clock size={15} />
      <span className="text-xs flex-1">{s > 0 ? t("order.pay_within") : t("order.pay_expired")}</span>
      {s > 0 && <span data-testid="pay-countdown-time" className="font-mono font-bold">{txt}</span>}
    </div>
  );
}

function BinancePayInfo({ o }) {
  const { t } = useI18n();
  return (
    <div data-testid="binance-pay-info" className="space-y-2 pt-1">
      {o.payment_qr_image && (
        <div className="flex flex-col items-center gap-2 pt-2">
          <div className="bg-white p-2 rounded-md"><img data-testid="binance-pay-qr" src={imgSrc(o.payment_qr_image)} alt="Binance Pay QR" className="w-52 h-52 object-contain" /></div>
          <div className="text-xs text-[#A8A8AD] text-center">{t("order.binance_scan")}</div>
        </div>
      )}
      <CopyRow label="Binance Pay UID" value={o.payment_details} testId="pay-binance-uid" />
      {o.payment_account_name && <div data-testid="pay-binance-name" className="text-xs text-[#A8A8AD]">{t("order.binance_name")}: <span className="text-[#F5F3ED]">{o.payment_account_name}</span></div>}
      <p className="text-xs text-[#F2D98D]">{t("order.binance_exact")}</p>
    </div>
  );
}

export default function PaymentQR({ o, onExpire }) {
  const { t } = useI18n();
  const isUsdt = o.payment_type === "crypto";
  const isBinPay = o.payment_type === "binance_pay";
  const asUsdt = o.payment_asset === "USDT" || isUsdt;
  const network = o.payment_network || "TRC20";
  const qrValue = o.qris_payload || (isUsdt ? o.payment_details : null);
  const due = o.amount_due_idr ?? o.total_idr;
  return (
    <div data-testid="payment-qr-panel" className="space-y-2 mb-3">
      {o.pay_deadline && <PayCountdown deadline={o.pay_deadline} onExpire={onExpire} />}
      {o.currency === "USD"
        ? <CopyRow label={`${t("order.amount_due")} (${o.payment_type === "paypal" ? "PayPal USD" : "USDT"})`} value={asUsdt ? Number(o.amount_due_usd).toFixed(2) : o.amount_due_usd} display={asUsdt ? fmtUSDT(o.amount_due_usd) : fmtUSD(o.amount_due_usd)} testId="pay-amount-usd" />
        : <CopyRow label={t("order.amount_due")} value={Math.round(due)} display={fmtIDR(due)} testId="pay-amount-idr" />}
      {o.unique_code > 0 && <p data-testid="pay-unique-note" className="text-xs text-[#F2D98D]">{t("order.transfer_exact").replace("{code}", o.unique_code)}</p>}
      {qrValue && (
        <div className="flex flex-col items-center gap-2 pt-2">
          <div className="bg-white p-3 rounded-md"><QRCodeSVG data-testid="payment-qr-code" value={qrValue} size={196} level="M" /></div>
          <div className="text-xs text-[#A8A8AD] text-center">{isUsdt ? t("order.scan_usdt") : t("order.scan_qris")}</div>
        </div>
      )}
      {asUsdt && o.fx_rate && <p data-testid="pay-usdt-rate" className="text-[11px] text-[#A8A8AD]">{t("order.usdt_locked").replace("{rate}", fmtIDR(o.fx_rate)).replace("{total}", fmtIDR(o.amount_due_idr ?? o.total_idr))}</p>}
      {isUsdt && <CopyRow label={`USDT · ${network}`} value={o.payment_details} testId="pay-usdt-address" />}
      {isUsdt && <p data-testid="pay-usdt-warning" className="text-xs text-[#F06464]">{t("order.usdt_network_warning").replace("{network}", network)}</p>}
      {isBinPay && <BinancePayInfo o={o} />}
    </div>
  );
}
